import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';
import AuthService from '../../services/authService';

const STATUS_OPTIONS = [
  { label: 'All statuses', value: 'all' },
  { label: 'Not started', value: 'not started' },
  { label: 'Ongoing', value: 'ongoing' },
  { label: 'Delivery completed', value: 'delivery completed' },
  { label: 'Batch completed', value: 'batch completed' },
];

const SUBMISSION_STATUS_OPTIONS = [
  'On-Time Submission',
  'Late Submission',
  'Resubmitted',
  'not submitted',
];

const MARK_OPTIONS = [
  { value: '', label: 'Not Corrected Yet' },
  { value: 'D', label: 'D - Distinction' },
  { value: 'M', label: 'M - Merit' },
  { value: 'P', label: 'P - Pass' },
  { value: 'R', label: 'R - Referred' },
];

const CODE_PRIORITY = {
  D: 1,
  M: 2,
  P: 3,
  SN: 4,
  R: 5,
  NS: 6,
};

// Match subject ordering used in HndExamBoard
const SOFTWARE_SUBJECT_ORDER = [
  'PRO', 'NET', 'PP', 'DDD', 'SDLC', 'PACP', 'Security', 'WDD',
  'CRP - I', 'BPS', 'UEID', 'SAD', 'APDP', 'DM', 'DSA', 'CRP - I',
];

const NETWORK_SUBJECT_ORDER = [
  'PRO', 'NET', 'PP', 'DDD', 'Maths', 'PACP', 'Security', 'CSA',
  'CRP - I', 'BPS', 'TND', 'CC', 'NS', 'IOT', 'ET', 'CRP - II',
];

function orderSubjectsForCourse(subjects, courseTitle) {
  if (!Array.isArray(subjects) || !subjects.length) return subjects || [];

  const normalizedTitle = (courseTitle || '').toLowerCase();
  const isSoftware = normalizedTitle === 'level 5 pearson btec hnd in computing (software engineering)';
  const isNetwork = normalizedTitle === 'level 5 pearson btec hnd in computing (network engineering)';
  const orderList = isSoftware ? SOFTWARE_SUBJECT_ORDER : (isNetwork ? NETWORK_SUBJECT_ORDER : null);

  const orderMap = orderList
    ? orderList.reduce((acc, code, idx) => {
        const key = (code || '').trim().toUpperCase();
        if (!(key in acc)) acc[key] = idx + 1;
        return acc;
      }, {})
    : null;

  const getKey = (s) => (s.subjectCode || s.subjectName || '').trim().toUpperCase();

  return [...subjects].sort((a, b) => {
    if (orderMap) {
      const oa = orderMap[getKey(a)] ?? 9999;
      const ob = orderMap[getKey(b)] ?? 9999;
      if (oa !== ob) return oa - ob;
    }

    const sa = parseInt(a.semester, 10) || 0;
    const sb = parseInt(b.semester, 10) || 0;
    if (sa !== sb) return sa - sb;

    return getKey(a).localeCompare(getKey(b));
  });
}

export default function HndAssignmentTracking() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batches, setBatches] = useState([]);
  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [students, setStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [subjectCols, setSubjectCols] = useState([]);
  const [dbSubmissionCount, setDbSubmissionCount] = useState(null);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [studentError, setStudentError] = useState(null);
  const [editModal, setEditModal] = useState({ open: false, student: null, subject: null, status: 'not submitted', marks: '' });
  const [savingSubmission, setSavingSubmission] = useState(false);
  const [uploadModal, setUploadModal] = useState({ open: false });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [parsedStudents, setParsedStudents] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [elmsModuleCodes, setElmsModuleCodes] = useState([]);
  const [loadingElmsCodes, setLoadingElmsCodes] = useState(false);
  const [showElmsModal, setShowElmsModal] = useState(false);
  const [elmsSearch, setElmsSearch] = useState('');
  const [excelMarks, setExcelMarks] = useState({}); // Store marks from Excel: { studentId: { subjectCode: mark } }
  const [editingExcelMark, setEditingExcelMark] = useState(null); // { studentId, subjectCode }
  const [editingExcelValue, setEditingExcelValue] = useState('');
  const [parsingFile, setParsingFile] = useState(false);
  const [creatingMissingSubmissions, setCreatingMissingSubmissions] = useState(false);
  const user = AuthService.getUser();
  const userRole = String(
    user?.superAdmin?.staff?.role ||
    user?.superAdmin?.role ||
    user?.superAdmin?.staff?.staffType ||
    user?.superAdmin?.staff?.type ||
    ''
  ).trim().toLowerCase();
  const isPlatformAdmin = Boolean(user?.superAdmin?.superAdminId) || userRole === 'admin' || userRole === 'administrator';
  const [repairStatus, setRepairStatus] = useState({
    loading: false,
    totalCount: 0,
    assignmentCount: 0,
    topupCount: 0,
    hasMissing: false,
  });
  const [repairingMarksUpdatedDate, setRepairingMarksUpdatedDate] = useState(false);

  const loadRepairStatus = async () => {
    if (!isPlatformAdmin) {
      setRepairStatus({
        loading: false,
        totalCount: 0,
        assignmentCount: 0,
        topupCount: 0,
        hasMissing: false,
      });
      return;
    }

    try {
      setRepairStatus((prev) => ({ ...prev, loading: true }));
      const { data } = await axiosInstance.get('/admin/hnd/assignment-tracking/missing-marks-updated-date-status');
      setRepairStatus({
        loading: false,
        totalCount: Number(data?.totalCount || 0),
        assignmentCount: Number(data?.assignmentCount || 0),
        topupCount: Number(data?.topupCount || 0),
        hasMissing: Boolean(data?.hasMissingMarksUpdatedDate),
      });
    } catch (err) {
      console.error('Failed to load marks_updated_date repair status', err);
      setRepairStatus({
        loading: false,
        totalCount: 0,
        assignmentCount: 0,
        topupCount: 0,
        hasMissing: false,
      });
    }
  };

  // Load coordinate course access for the logged-in user
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;

        const data = accessRes.data || {};
        setCoordinateCourses(data.coordinateCourses || {});
      } catch (e) {
        console.error('Failed to load coordinate courses for HndAssignmentTracking', e);
        setCoordinateCourses({});
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    loadRepairStatus();
  }, [isPlatformAdmin]);

  const enrichStudentsWithSubmissions = async (baseStudents, subjects) => {
    if (!Array.isArray(baseStudents) || !baseStudents.length || !Array.isArray(subjects) || !subjects.length) {
      return baseStudents || [];
    }

    const codeByBatchSubjectId = new Map(
      subjects.map((s) => [Number(s.subjectId), s.subjectCode])
    );

    const enriched = await Promise.all(
      baseStudents.map(async (stu) => {
        const studentId = stu.studentId;
        if (!studentId) return { ...stu, marksBySubject: stu.marksBySubject || {}, statusBySubject: stu.statusBySubject || {}, feedbackBySubject: stu.feedbackBySubject || {} };

        try {
          const { data } = await axiosInstance.get(`/admin/students/${studentId}/submissions`);
          const submissions = Array.isArray(data?.submissions) ? data.submissions : [];
          const marksBySubject = {};
          const statusBySubject = {};
          const feedbackBySubject = {};

          submissions.forEach((sub) => {
            const batchSubjectId = Number(sub.batch_subjectId ?? sub.batchSubjectId);
            if (!Number.isInteger(batchSubjectId)) return;
            const code = codeByBatchSubjectId.get(batchSubjectId);
            if (!code) return;

            if (sub.marks !== null && sub.marks !== undefined && sub.marks !== '') {
              marksBySubject[code] = String(sub.marks).trim();
            }
            if (sub.submission_status !== null && sub.submission_status !== undefined && sub.submission_status !== '') {
              statusBySubject[code] = sub.submission_status;
            }
            if (sub.feedback !== null && sub.feedback !== undefined) {
              feedbackBySubject[code] = String(sub.feedback).trim();
            }
          });

          return {
            ...stu,
            marksBySubject,
            statusBySubject,
            feedbackBySubject,
          };
        } catch (e) {
          // If per-student submissions fail, keep the original student data.
          return { ...stu, marksBySubject: stu.marksBySubject || {}, statusBySubject: stu.statusBySubject || {}, feedbackBySubject: stu.feedbackBySubject || {} };
        }
      })
    );

    return enriched;
  };

  // Fetch ELMS module codes on page load
  useEffect(() => {
    const controller = new AbortController();
    const fetchElmsModuleCodes = async () => {
      try {
        setLoadingElmsCodes(true);
        const { data } = await axiosInstance.get('/admin/elms-module-codes/list', { signal: controller.signal });
        setElmsModuleCodes(Array.isArray(data) ? data : []);
        console.log('Loaded ELMS module codes:', data);
      } catch (e) {
        if (e.name !== 'CanceledError') {
          console.error('Failed to load ELMS module codes:', e);
        }
      } finally {
        setLoadingElmsCodes(false);
      }
    };
    fetchElmsModuleCodes();
    return () => controller.abort();
  }, []);

  const filteredElmsModules = useMemo(() => {
    if (!Array.isArray(elmsModuleCodes)) return [];
    const q = elmsSearch.trim().toLowerCase();
    if (!q) return elmsModuleCodes;
    return elmsModuleCodes.filter((row) => {
      const elms = String(row.elms_module_code || '').toLowerCase();
      const moduleCode = String(row.module_code || '').toLowerCase();
      const subjectFull = String(row.subject_full_name || row.subjectFullName || '').toLowerCase();
      const subjectShort = String(row.subject_short_name || row.subjectShortName || '').toLowerCase();
      return (
        elms.includes(q) ||
        moduleCode.includes(q) ||
        subjectFull.includes(q) ||
        subjectShort.includes(q)
      );
    });
  }, [elmsModuleCodes, elmsSearch]);

  // Fetch HND batches (uses existing /admin/batches/hnd endpoint)
  useEffect(() => {
    const controller = new AbortController();
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {};
        if (status && status !== 'all') params.status = status;
        if (search.trim()) params.search = search.trim();
        const { data } = await axiosInstance.get('/admin/batches/hnd', { params, signal: controller.signal });
        setBatches(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name !== 'CanceledError') {
          setError(e?.response?.data?.message || e.message || 'Failed to load batches');
        }
      } finally {
        setLoading(false);
      }
    };
    run();
    return () => controller.abort();
  }, [status, search]);

  // Course IDs for which the user is a coordinator
  const coordinateCourseIds = useMemo(() => {
    if (!coordinateCourses || typeof coordinateCourses !== 'object') return [];
    return Object.keys(coordinateCourses)
      .map((id) => Number(id))
      .filter((id) => !Number.isNaN(id));
  }, [coordinateCourses]);

  // Restrict HND batches to only those that belong to the user's coordinate courses (if any)
  const filteredBatches = useMemo(() => {
    if (!Array.isArray(batches)) return [];
    if (!coordinateCourseIds.length) return batches;
    return batches.filter((b) => {
      if (b.courseId === null || b.courseId === undefined) return false;
      const courseIdNum = Number(b.courseId);
      return !Number.isNaN(courseIdNum) && coordinateCourseIds.includes(courseIdNum);
    });
  }, [batches, coordinateCourseIds]);

  const groupedOptions = useMemo(() => {
    const opts = filteredBatches.map((b) => ({
      value: b.batchId,
      label: `${b.batchName} - ${b.courseShortName || b.courseName || ''}${b.batchInternalName ? ` (IN: ${b.batchInternalName})` : ''}`,
      group: b.whatsappGroupName || 'No Group',
    }));
    const byGroup = {};
    opts.forEach((o) => {
      byGroup[o.group] = byGroup[o.group] || [];
      byGroup[o.group].push(o);
    });
    return Object.entries(byGroup)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([label, options]) => ({ label, options: options.sort((x, y) => x.label.localeCompare(y.label)) }));
      }, [filteredBatches]);

  // Load students + subject columns for selected batch (expects backend to provide marks/status per subject)
  useEffect(() => {
    if (!selected) return;
    const controller = new AbortController();
    const run = async () => {
      try {
        setLoadingStudents(true);
        setStudentError(null);
        const params = { batchIds: selected.value };
        const { data } = await axiosInstance.get('/admin/hnd/assignment-tracking/students', { params, signal: controller.signal });

        const baseStudents = data.students || [];

        const rawSubjects = (data.subjects || []).map((s) => ({
          subjectId: s.subjectId,
          subjectCode: s.subjectCode,
          subjectName: s.subjectName,
          semester: s.semester,
          lecturerName: s.lecturerName || '',
          lecturerImagePath: s.lecturerImagePath || null,
          actualAssignmentSubmissionDate: s.actualAssignmentSubmissionDate || null,
        }));

        const courseTitle = baseStudents[0]?.courseName || baseStudents[0]?.courseShortName || '';
        const subjects = orderSubjectsForCourse(rawSubjects, courseTitle);

        const enrichedStudents = await enrichStudentsWithSubmissions(baseStudents, subjects);

        setSubjectCols(subjects);
        setStudents(enrichedStudents);
        setDbSubmissionCount(typeof data.submissionRowCount === 'number' ? data.submissionRowCount : null);
      } catch (e) {
        if (e.name !== 'CanceledError') {
          setStudentError(e?.response?.data?.message || e.message || 'Failed to load students');
          setSubjectCols([]);
          setStudents([]);
        }
      } finally {
        setLoadingStudents(false);
      }
    };
    run();
    return () => controller.abort();
  }, [selected]);

  // If the selected batch is no longer within the filtered coordinate-course batches, clear selection
  useEffect(() => {
    if (!selected) return;
    const stillExists = filteredBatches.some((b) => Number(b.batchId) === Number(selected.value));
    if (!stillExists) {
      setSelected(null);
      setStudents([]);
      setSubjectCols([]);
      setDbSubmissionCount(null);
    }
  }, [filteredBatches, selected]);

  const selectedBatchInfo = useMemo(() => {
    if (!selected) return null;
    const match = batches.find((b) => Number(b.batchId) === Number(selected.value));
    return match || null;
  }, [selected, batches]);

  const handleClearSelection = () => {
    setSelected(null);
    setStudents([]);
    setSubjectCols([]);
    setDbSubmissionCount(null);
  };

  const refreshStudentsForSelectedBatch = async () => {
    if (!selected) return;
    try {
      setLoadingStudents(true);
      setStudentError(null);
      const params = { batchIds: selected.value };
      const { data } = await axiosInstance.get('/admin/hnd/assignment-tracking/students', { params });

      const baseStudents = data.students || [];
      const rawSubjects = (data.subjects || []).map((s) => ({
        subjectId: s.subjectId,
        subjectCode: s.subjectCode,
        subjectName: s.subjectName,
        semester: s.semester,
        lecturerName: s.lecturerName || '',
        lecturerImagePath: s.lecturerImagePath || null,
        actualAssignmentSubmissionDate: s.actualAssignmentSubmissionDate || null,
      }));

      const courseTitle = baseStudents[0]?.courseName || baseStudents[0]?.courseShortName || '';
      const subjects = orderSubjectsForCourse(rawSubjects, courseTitle);
      const enrichedStudents = await enrichStudentsWithSubmissions(baseStudents, subjects);

      setSubjectCols(subjects);
      setStudents(enrichedStudents);
      setDbSubmissionCount(typeof data.submissionRowCount === 'number' ? data.submissionRowCount : null);
    } catch (e) {
      setStudentError(e?.response?.data?.message || e.message || 'Failed to refresh students');
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleRemoveStudent = (studentId) => {
    Swal.fire({
      title: 'Remove student from view?',
      text: `Student ${studentId} will be hidden from this list (no data is modified).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Hide',
    }).then((r) => {
      if (r.isConfirmed) {
        setStudents((prev) => prev.filter((s) => String(s.studentId) !== String(studentId)));
      }
    });
  };

  const expectedSubmissionCount = useMemo(() => {
    if (!students.length || !subjectCols.length) return 0;
    return students.length * subjectCols.length;
  }, [students, subjectCols]);

  const handleCreateMissingSubmissions = async () => {
    if (!selected || !expectedSubmissionCount) return;

    const mismatch =
      dbSubmissionCount === null || dbSubmissionCount !== expectedSubmissionCount;

    if (!mismatch) return;

    const confirm = await Swal.fire({
      title: 'Create submission details?',
      html:
        `<div class="text-sm text-gray-800 text-left space-y-1">` +
        `<p>This will create missing assignment submission rows for all students and subjects in this batch.</p>` +
        `<p>New rows will be created with <strong>submission_status = "not submitted"</strong>.</p>` +
        `<p class="text-xs text-gray-600">Existing records will not be modified.</p>` +
        `</div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, create',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a',
    });

    if (!confirm.isConfirmed) return;

    try {
      setCreatingMissingSubmissions(true);
      const payload = { batchId: selected.value };
      const { data } = await axiosInstance.post(
        '/admin/hnd/assignment-tracking/create-missing-submissions',
        payload
      );

      const inserted = data?.inserted ?? 0;
      const message =
        data?.message ||
        (inserted > 0
          ? `Created ${inserted} missing assignment submission record(s).`
          : 'No missing assignment submissions were found for this batch.');

      await Swal.fire({
        icon: inserted > 0 ? 'success' : 'info',
        title: inserted > 0 ? 'Submission details created' : 'Nothing to create',
        text: message,
      });

      // Refresh students & counts so Expected/Existing reflects latest state
      await refreshStudentsForSelectedBatch();
    } catch (err) {
      const message =
        err?.response?.data?.message || err.message || 'Failed to create missing submissions.';
      Swal.fire('Error', message, 'error');
    } finally {
      setCreatingMissingSubmissions(false);
    }
  };

  const handleBackfillMarksUpdatedDate = async () => {
    if (!isPlatformAdmin || !repairStatus.hasMissing || repairingMarksUpdatedDate) return;

    const confirm = await Swal.fire({
      title: 'Fix missing marks updated dates?',
      html:
        `<div class="text-sm text-gray-800 text-left space-y-1">` +
        `<p>This will set <strong>marks_updated_date</strong> to the current date/time for all rows where:</p>` +
        `<p><strong>submission_status</strong> is not <strong>not submitted</strong> and <strong>marks_updated_date</strong> is null.</p>` +
        `<p class="text-xs text-gray-600">Regular: ${repairStatus.assignmentCount} | TOPUP: ${repairStatus.topupCount}</p>` +
        `</div>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, fix all',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
    });

    if (!confirm.isConfirmed) return;

    try {
      setRepairingMarksUpdatedDate(true);
      const { data } = await axiosInstance.post('/admin/hnd/assignment-tracking/backfill-marks-updated-date');

      await Swal.fire({
        icon: 'success',
        title: 'Repair completed',
        html:
          `<div class="text-left text-sm space-y-1">` +
          `<p>Regular updated: <strong>${Number(data?.assignmentUpdated || 0)}</strong></p>` +
          `<p>TOPUP updated: <strong>${Number(data?.topupUpdated || 0)}</strong></p>` +
          `<p>Total updated: <strong>${Number(data?.totalUpdated || 0)}</strong></p>` +
          `</div>`,
      });

      await loadRepairStatus();
      if (selected) {
        await refreshStudentsForSelectedBatch();
      }
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to fix marks updated dates.';
      Swal.fire('Error', message, 'error');
    } finally {
      setRepairingMarksUpdatedDate(false);
    }
  };

  const displayForSubject = (st, col) => {
    const status = st?.statusBySubject?.[col.subjectCode];
    const rawMark = st?.marksBySubject?.[col.subjectCode];
    const mark = normalizeMark(rawMark);
    if (mark) return mark;
    if (status && status !== 'not submitted') return 'SN';
    return 'NS';
  };

  const normalizeMark = (raw) => {
    if (raw === null || raw === undefined) return '';
    const val = String(raw).trim();
    if (!val) return '';
    const upper = val.toUpperCase();

    // Handle composite ELMS patterns explicitly
    // R,RP  -> final P
    // R,R,RN -> final R
    // R,RF -> final R
    const compact = upper.replace(/\s+/g, '');
    if (compact === 'R,RP') return 'P';
    if (compact === 'R,R,RN') return 'R';
    if (compact === 'R,RF') return 'R';

    if (upper === 'D' || upper.startsWith('DIST')) return 'D';
    if (upper === 'M' || upper.startsWith('MERIT')) return 'M';
    if (upper === 'P' || upper.startsWith('PASS')) return 'P';
    if (upper === 'R' || upper.startsWith('REF')) return 'R';
    return upper; // fallback: show whatever came from DB
  };

  const parseLocalDate = (value) => {
    if (!value) return null;
    const trimmed = String(value).trim();
    if (!trimmed) return null;

    // Handle plain date strings (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      const [y, m, d] = trimmed.split('-').map(Number);
      if ([y, m, d].some((n) => Number.isNaN(n))) return null;
      return new Date(y, m - 1, d);
    }

    // Handle ISO date-time strings (ignore timezone part)
    if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
      const [datePart] = trimmed.split('T');
      const [y, m, d] = datePart.split('-').map(Number);
      if ([y, m, d].some((n) => Number.isNaN(n))) return null;
      return new Date(y, m - 1, d);
    }

    const parsed = new Date(trimmed);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatDate = (value) => {
    const d = parseLocalDate(value);
    if (!d) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getUniversityRegisterInfo = (value) => {
    const parsed = parseLocalDate(value);
    if (!parsed) return null;

    const formattedDate = parsed.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });

    const now = new Date();
    let months = (now.getFullYear() - parsed.getFullYear()) * 12 + (now.getMonth() - parsed.getMonth());

    if (now.getDate() < parsed.getDate()) {
      months -= 1;
    }
    if (months < 0) months = 0;

    const monthsLabel = months === 1 ? '1 month' : `${months} months`;

    return { formattedDate, monthsLabel, monthsCount: months };
  };

  const chipClass = (code) => {
    if (code === 'D') return 'bg-emerald-100 text-emerald-800';
    if (code === 'M') return 'bg-green-100 text-green-800';
    if (code === 'P') return 'bg-lime-100 text-lime-800';
    if (code === 'R') return 'bg-rose-100 text-rose-800';
    if (code === 'SN') return 'bg-amber-50 text-amber-700';
    return 'bg-slate-100 text-slate-700';
  };

  const getAttendancePercent = (st) => {
    const raw = st?.attendancePercentage
      ?? st?.attendancePercent
      ?? st?.attendance_percentage
      ?? st?.attendance_percent
      ?? st?.totalAttendancePercentage
      ?? st?.attendance?.percentage
      ?? st?.attendance?.overall
      ?? st?.overallAttendance
      ?? st?.attendanceOverall;

    if (raw === null || raw === undefined) return null;

    const cleaned = typeof raw === 'string' ? raw.replace(/%/g, '').trim() : raw;
    const num = Number(cleaned);
    if (!Number.isFinite(num)) return null;

    const pct = num <= 1 ? num * 100 : num;
    const clamped = Math.max(0, Math.min(100, pct));
    return Math.round(clamped * 10) / 10;
  };

  const computeStudentStats = (st) => {
    const counts = { D: 0, M: 0, P: 0, SN: 0, R: 0, NS: 0 };
    let totalScore = 0;
    let bestRank = CODE_PRIORITY.NS;

    subjectCols.forEach((col) => {
      const code = displayForSubject(st, col);
      const normalizedCode = CODE_PRIORITY[code] ? code : 'NS';
      const rank = CODE_PRIORITY[normalizedCode];
      counts[normalizedCode] = (counts[normalizedCode] || 0) + 1;
      totalScore += rank;
      if (rank < bestRank) bestRank = rank;
    });

    return { totalScore, counts, bestRank };
  };

  const filteredStudents = useMemo(() => {
    const term = studentSearch.trim().toLowerCase();
    if (!term) return students;

    return students.filter((stu) => {
      const fields = [
        stu.fullName,
        stu.name,
        stu.studentId,
        stu.id,
        stu.registrationNumber,
        stu.student_registration_number,
        stu.telephone,
        stu.phone,
        stu.email,
      ];

      return fields.some((value) => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(term);
      });
    });
  }, [students, studentSearch]);

  const sortedStudents = useMemo(() => {
    if (!filteredStudents.length) return [];
    if (!subjectCols.length) return [...filteredStudents];

    const statsList = filteredStudents.map((student) => ({ student, stats: computeStudentStats(student) }));

    statsList.sort((a, b) => {
      const sa = a.stats;
      const sb = b.stats;

      if (sa.totalScore !== sb.totalScore) return sa.totalScore - sb.totalScore;
      if (sa.counts.D !== sb.counts.D) return sb.counts.D - sa.counts.D;
      if (sa.counts.M !== sb.counts.M) return sb.counts.M - sa.counts.M;
      if (sa.counts.P !== sb.counts.P) return sb.counts.P - sa.counts.P;
      if (sa.counts.SN !== sb.counts.SN) return sa.counts.SN - sb.counts.SN;
      if (sa.counts.R !== sb.counts.R) return sa.counts.R - sb.counts.R;
      if (sa.counts.NS !== sb.counts.NS) return sa.counts.NS - sb.counts.NS;
      if (sa.bestRank !== sb.bestRank) return sa.bestRank - sb.bestRank;
      return (a.student.fullName || '').localeCompare(b.student.fullName || '');
    });

    return statsList.map((entry) => entry.student);
  }, [filteredStudents, subjectCols]);

  const openSubmissionModal = (student, subject) => {
    if (!student || !subject) return;
    const currentStatus = student?.statusBySubject?.[subject.subjectCode] || 'not submitted';
    const currentMarks = student?.marksBySubject?.[subject.subjectCode] ?? '';
    setEditModal({ open: true, student, subject, status: currentStatus, marks: currentMarks ?? '' });
  };

  const closeSubmissionModal = () => {
    setEditModal({ open: false, student: null, subject: null, status: 'not submitted', marks: '' });
  };

  const handleSaveSubmission = async () => {
    if (!editModal.student || !editModal.subject) return;
    try {
      setSavingSubmission(true);
      const payload = {
        studentId: editModal.student.studentId,
        batchSubjectId: editModal.subject.subjectId,
        submission_status: editModal.status,
        marks: editModal.marks,
      };
      await axiosInstance.put('/admin/hnd/assignment-tracking/submission', payload);

      setStudents((prev) => prev.map((s) => {
        if (String(s.studentId) !== String(editModal.student.studentId)) return s;
        const nextMarks = { ...(s.marksBySubject || {}) };
        const nextStatus = { ...(s.statusBySubject || {}) };

        if (editModal.marks === '' || editModal.marks === null || editModal.marks === undefined) {
          delete nextMarks[editModal.subject.subjectCode];
        } else {
          nextMarks[editModal.subject.subjectCode] = editModal.marks;
        }

        if (!editModal.status) {
          delete nextStatus[editModal.subject.subjectCode];
        } else {
          nextStatus[editModal.subject.subjectCode] = editModal.status;
        }

        return { ...s, marksBySubject: nextMarks, statusBySubject: nextStatus };
      }));

      Swal.fire('Saved', 'Assignment submission updated.', 'success');
      closeSubmissionModal();
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to save submission.';
      Swal.fire('Error', message, 'error');
    } finally {
      setSavingSubmission(false);
    }
  };

  const openUploadModal = () => {
    if (!selected) {
      Swal.fire('Error', 'Please select a batch first.', 'warning');
      return;
    }
    setUploadModal({ open: true });
    setUploadFile(null);
    setUploadProgress(0);
    setParsedStudents([]);
    setParseError(null);
    setExcelMarks({});
    setEditingExcelMark(null);
    setEditingExcelValue('');
    setParsingFile(false);
  };

  const closeUploadModal = () => {
    if (uploading) {
      Swal.fire('Warning', 'Please wait for the upload to complete.', 'warning');
      return;
    }
    setUploadModal({ open: false });
    setUploadFile(null);
    setUploadProgress(0);
    setParsedStudents([]);
    setParseError(null);
    setExcelMarks({});
    setEditingExcelMark(null);
    setEditingExcelValue('');
    setParsingFile(false);
  };

  const handleEditExcelMark = (studentId, subjectCode, currentValue) => {
    setEditingExcelMark({ studentId, subjectCode });
    setEditingExcelValue(currentValue || '');
  };

  const handleSaveExcelMark = (studentId, subjectCode) => {
    setExcelMarks(prev => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        [subjectCode]: editingExcelValue.trim()
      }
    }));
    setEditingExcelMark(null);
    setEditingExcelValue('');
  };

  const handleCancelExcelEdit = () => {
    setEditingExcelMark(null);
    setEditingExcelValue('');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/i)) {
      Swal.fire('Invalid File', 'Please upload an Excel (.xlsx, .xls) or CSV file.', 'error');
      e.target.value = '';
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      Swal.fire('File Too Large', 'Please upload a file smaller than 10MB.', 'error');
      e.target.value = '';
      return;
    }

    setUploadFile(file);
    setParsedStudents([]);
    setParseError(null);
    setExcelMarks({});
    setParsingFile(true);

    // Parse the file to extract registration numbers
    try {
      const XLSX = await import('xlsx');
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

          if (!jsonData || jsonData.length === 0) {
            setParseError('No data found in the worksheet.');
            return;
          }

          // Find the registration number column
          const sampleRow = jsonData[0];
          let regNoColumn = null;
          
          Object.keys(sampleRow).forEach(key => {
            const normalized = key.toLowerCase().trim();
            if (normalized.includes('registration') && (normalized.includes('no') || normalized.includes('number'))) {
              regNoColumn = key;
            }
          });

          if (!regNoColumn) {
            setParseError('Could not find "Student Registration No" column in the file.');
            return;
          }

          // Map ELMS module codes to subject codes
          const elmsToSubjectMap = new Map();
          elmsModuleCodes.forEach(elms => {
            if (elms.elms_module_code && elms.subject?.shortName) {
              elmsToSubjectMap.set(elms.elms_module_code.trim().toUpperCase(), elms.subject.shortName.trim().toUpperCase());
            }
          });

          // Find matching ELMS columns in the Excel sheet
          const elmsColumns = [];
          Object.keys(sampleRow).forEach(key => {
            const normalizedKey = key.trim().toUpperCase();
            if (elmsToSubjectMap.has(normalizedKey)) {
              elmsColumns.push({
                excelColumn: key,
                elmsCode: normalizedKey,
                subjectCode: elmsToSubjectMap.get(normalizedKey)
              });
            }
          });

          // Extract unique registration numbers
          const regNumbers = [...new Set(
            jsonData
              .map(row => String(row[regNoColumn] || '').trim().toUpperCase())
              .filter(regNo => regNo && regNo !== '')
          )];

          // Match with existing students and extract Excel marks
          const matchedStudents = [];
          const excelMarksData = {};

          students.forEach(student => {
            const studentRegNo = String(student.registrationNumber || '').trim().toUpperCase();
            const studentId = String(student.studentId || '').trim().toUpperCase();
            
            if (regNumbers.includes(studentRegNo) || regNumbers.includes(studentId)) {
              matchedStudents.push(student);

              // Find this student's row in Excel
              const studentRow = jsonData.find(row => {
                const rowRegNo = String(row[regNoColumn] || '').trim().toUpperCase();
                return rowRegNo === studentRegNo || rowRegNo === studentId;
              });

              if (studentRow) {
                // Extract marks for matched ELMS columns
                const studentMarks = {};
                elmsColumns.forEach(col => {
                  const rawValue = studentRow[col.excelColumn];
                  if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
                    let normalizedMark = String(rawValue).trim().toUpperCase();
                    // Replace 'F' with 'R'
                    if (normalizedMark === 'F' || normalizedMark.startsWith('FAIL')) {
                      normalizedMark = 'R';
                    }
                    if (normalizedMark) {
                      studentMarks[col.subjectCode] = normalizedMark;
                    }
                  }
                });
                excelMarksData[student.studentId] = studentMarks;
              }
            }
          });

          if (matchedStudents.length === 0) {
            setParseError('No matching students found in the current batch.');
            setParsedStudents([]);
            setExcelMarks({});
          } else {
            setParsedStudents(matchedStudents);
            setExcelMarks(excelMarksData);
            setParseError(null);
          }
          setParsingFile(false);
        } catch (err) {
          setParseError(`Failed to parse file: ${err.message}`);
          setParsedStudents([]);
          setExcelMarks({});
          setParsingFile(false);
        }
      };

      reader.onerror = () => {
        setParseError('Failed to read file.');
        setParsedStudents([]);
        setExcelMarks({});
        setParsingFile(false);
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      setParseError(`Error loading file parser: ${err.message}`);
      setParsedStudents([]);
      setExcelMarks({});
      setParsingFile(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      Swal.fire('No File', 'Please select a file to upload.', 'warning');
      return;
    }

    if (!selected) {
      Swal.fire('No Batch', 'Please select a batch first.', 'warning');
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(10);

      // Prepare mismatched marks data
      const mismatchedMarks = [];
      parsedStudents.forEach(student => {
        subjectCols.forEach(col => {
          const dbCode = displayForSubject(student, col);
          const excelMark = excelMarks[student.studentId]?.[col.subjectCode];
          const hasExcelMark = excelMark !== null && excelMark !== undefined && excelMark !== '';
          const marksMatch = hasExcelMark && normalizeMark(excelMark) === dbCode;
          
          // Only include mismatched marks
          if (hasExcelMark && !marksMatch) {
            mismatchedMarks.push({
              studentId: student.studentId,
              batchSubjectId: col.subjectId,
              excelMark: normalizeMark(excelMark)
            });
          }
        });
      });

      if (mismatchedMarks.length === 0) {
        Swal.fire('No Mismatches', 'No mismatched marks found to update.', 'info');
        setUploading(false);
        return;
      }

      setUploadProgress(30);

      const payload = {
        batchId: selected.value,
        mismatchedMarks
      };

      const { data } = await axiosInstance.post('/admin/hnd/assignment-tracking/update-mismatched-marks', payload);

      setUploadProgress(100);

      Swal.fire({
        icon: 'success',
        title: 'Upload Successful',
        html: `
          <div class="text-left text-sm space-y-2">
            <p><strong>Processed:</strong> ${data.processed || 0} records</p>
            <p><strong>Updated:</strong> ${data.updated || 0} submissions</p>
            <p><strong>Skipped:</strong> ${data.skipped || 0} records</p>
            ${data.errors && data.errors.length > 0 ? `
              <p class="text-red-600"><strong>Errors:</strong> ${data.errors.length}</p>
              <div class="max-h-32 overflow-y-auto text-xs">
                ${data.errors.slice(0, 10).map(err => `<div>• ${err}</div>`).join('')}
                ${data.errors.length > 10 ? `<div>... and ${data.errors.length - 10} more</div>` : ''}
              </div>
            ` : ''}
          </div>
        `,
        confirmButtonText: 'OK'
      });

      // Refresh students data
      if (data.updated > 0) {
        // Trigger a refresh by re-fetching
        const params = { batchIds: selected.value };
        const refreshData = await axiosInstance.get('/admin/hnd/assignment-tracking/students', { params });
        const baseStudents = refreshData.data.students || [];
        const rawSubjects = (refreshData.data.subjects || []).map((s) => ({
          subjectId: s.subjectId,
          subjectCode: s.subjectCode,
          subjectName: s.subjectName,
          semester: s.semester,
          lecturerName: s.lecturerName || '',
          lecturerImagePath: s.lecturerImagePath || null,
          actualAssignmentSubmissionDate: s.actualAssignmentSubmissionDate || null,
        }));
        const courseTitle = baseStudents[0]?.courseName || baseStudents[0]?.courseShortName || '';
        const subjects = orderSubjectsForCourse(rawSubjects, courseTitle);
        const enrichedStudents = await enrichStudentsWithSubmissions(baseStudents, subjects);
        setStudents(enrichedStudents);
      }

      closeUploadModal();
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Failed to upload ELMS sheet.';
      const details = err?.response?.data?.details || err?.response?.data?.errors || [];
      
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        html: `
          <div class="text-left text-sm space-y-2">
            <p>${message}</p>
            ${details.length > 0 ? `
              <div class="max-h-32 overflow-y-auto text-xs text-red-600">
                ${details.slice(0, 5).map(err => `<div>• ${err}</div>`).join('')}
                ${details.length > 5 ? `<div>... and ${details.length - 5} more</div>` : ''}
              </div>
            ` : ''}
          </div>
        `
      });
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                placeholder="Search by batch, internal or WhatsApp group name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quick Actions</label>
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-indigo-600 to-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:from-indigo-700 hover:to-blue-700 transition-all"
                  onClick={() => setShowElmsModal(true)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Show ELMS Modules
                </button>
                {isPlatformAdmin && repairStatus.hasMissing && (
                  <button
                    type="button"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-gradient-to-r from-rose-600 to-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:from-rose-700 hover:to-red-700 transition-all disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={handleBackfillMarksUpdatedDate}
                    disabled={repairingMarksUpdatedDate || repairStatus.loading}
                    title={`Regular: ${repairStatus.assignmentCount}, TOPUP: ${repairStatus.topupCount}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {repairingMarksUpdatedDate ? 'Fixing dates...' : `Fix Missing Marks Dates (${repairStatus.totalCount})`}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="pt-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Assign HND Batches by WhatsApp Group</label>
            <Select
              isLoading={loading}
              options={groupedOptions}
              value={selected}
              onChange={(val) => setSelected(val || null)}
              classNamePrefix="react-select"
              placeholder="Select a batch (grouped by WhatsApp group)..."
              styles={{ menu: (base) => ({ ...base, zIndex: 40 }) }}
              isClearable
            />
            {error && (
              <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
            {!loading && filteredBatches.length === 0 && !error && (
              <p className="mt-2 text-sm text-gray-600">No HND batches found for your coordinate courses.</p>
            )}
          </div>

          <div className="pt-4 border-t">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center rounded-md bg-indigo-50 px-2.5 py-1.5 font-medium text-indigo-700 border border-indigo-200">
                Selected: {selected ? 1 : 0}
              </span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-700">Group: {selected ? selected.group : '-'}</span>
            </div>

            {selected && (
              <div className="mt-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">WhatsApp Group</th>
                      <th className="px-3 py-2 text-left font-semibold text-gray-700">Batch</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    <tr key={selected.value}>
                      <td className="px-3 py-2 text-gray-800">{selected.group}</td>
                      <td className="px-3 py-2 text-gray-800">{selected.label}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                onClick={handleClearSelection}
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {showElmsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-4">
            <div
              className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity"
              aria-hidden="true"
              onClick={() => { setShowElmsModal(false); setElmsSearch(''); }}
            ></div>
            <div className="relative z-50 w-full max-w-5xl transform overflow-hidden rounded-lg bg-white shadow-xl">
              <div className="border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">ELMS Modules</h2>
                  <p className="text-xs text-gray-600">Search ELMS module mappings by ELMS code, module code, or subject.</p>
                </div>
                <button
                  onClick={() => { setShowElmsModal(false); setElmsSearch(''); }}
                  className="rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative w-full sm:max-w-md">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={elmsSearch}
                      onChange={(e) => setElmsSearch(e.target.value)}
                      placeholder="Search by ELMS/module/subject..."
                      className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  {loadingElmsCodes && (
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-indigo-500 border-t-transparent" />
                      Loading ELMS modules...
                    </div>
                  )}
                </div>

                <div className="max-h-[60vh] overflow-y-auto rounded-lg border border-gray-200">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">#</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Subject</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Module Code</th>
                        <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">ELMS Module Code</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {filteredElmsModules.length ? (
                        filteredElmsModules.map((row, idx) => (
                          <tr key={row.elms_module_code_id || `${row.elms_module_code || ''}-${idx}`} className="hover:bg-gray-50">
                            <td className="px-3 py-2 align-top text-xs text-gray-500">{idx + 1}</td>
                            <td className="px-3 py-2 align-top">
                              <div className="text-sm font-medium text-gray-900">{row.subject_full_name || row.subjectFullName || '-'}</div>
                              <div className="text-xs text-gray-500">{row.subject_short_name || row.subjectShortName || ''}</div>
                            </td>
                            <td className="px-3 py-2 align-top text-sm font-medium text-gray-800">{row.module_code || '-'}</td>
                            <td className="px-3 py-2 align-top text-sm text-gray-800">{row.elms_module_code || '-'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className="px-3 py-8 text-center text-sm text-gray-500" colSpan={4}>
                            {loadingElmsCodes ? 'Loading ELMS modules...' : (elmsSearch ? 'No modules match your search.' : 'No ELMS modules found.')}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end border-t border-gray-100 pt-3 mt-2">
                  <button
                    type="button"
                    className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    onClick={() => { setShowElmsModal(false); setElmsSearch(''); }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {selected && (
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Selected batch</p>
                <p className="text-lg font-semibold text-gray-900">{selected.label}</p>
              </div>
              {selectedBatchInfo && (
                <div className="text-sm text-gray-600 text-right">
                  <div>Status: {selectedBatchInfo.status || 'N/A'}</div>
                  <div>WhatsApp: {selectedBatchInfo.whatsappGroupName || 'N/A'}</div>
                  {selectedBatchInfo.batchStartingDate && <div>Start: {selectedBatchInfo.batchStartingDate}</div>}
                </div>
              )}
            </div>

            <div className="border-t pt-4 space-y-3">
              <div className="flex items-center flex-wrap gap-3">
                <h2 className="text-lg font-semibold text-gray-900">Students</h2>
                <div className="flex items-center gap-2 text-sm">
                  <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-gray-800 border border-gray-200" title="Expected submissions = students × subjects">
                    Expected: {expectedSubmissionCount}
                  </span>
                  <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-1 text-gray-800 border border-gray-200" title="Existing submission records found">
                    Existing: {dbSubmissionCount ?? '—'}
                  </span>
                  {dbSubmissionCount !== null && expectedSubmissionCount > 0 && dbSubmissionCount >= expectedSubmissionCount && (
                    <span className="inline-flex items-center rounded-md bg-emerald-100 px-2 py-1 text-emerald-800 border border-emerald-200">All good</span>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  {expectedSubmissionCount > 0 && (dbSubmissionCount === null || dbSubmissionCount !== expectedSubmissionCount) && (
                    <button
                      type="button"
                      className="inline-flex items-center gap-2 rounded-md bg-amber-500 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-600 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleCreateMissingSubmissions}
                      disabled={creatingMissingSubmissions || loadingStudents}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      {creatingMissingSubmissions ? 'Creating...' : 'Create submission detail'}
                    </button>
                  )}
                  {loadingStudents && <span className="text-sm text-gray-500">Loading...</span>}
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={openUploadModal}
                    disabled={!selected || loadingStudents}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    Upload ELMS Sheet
                  </button>
                </div>
              </div>

              <div className="w-full mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="hnd-student-search">Student search</label>
                <div className="relative">
                  <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 10a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    id="hnd-student-search"
                    type="text"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Search by name, student ID, registration #, or telephone"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Filter by any student identifier or phone number.</p>
              </div>

              {studentError && <div className="text-sm text-red-600">{studentError}</div>}
              {!loadingStudents && students.length === 0 && (
                <div className="text-sm text-gray-600">No students found for this batch.</div>
              )}
              {students.length > 0 && !loadingStudents && filteredStudents.length === 0 && studentSearch.trim() && (
                <div className="text-sm text-gray-600">No students match your search.</div>
              )}

              {sortedStudents.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-left">
                    <thead>
                      <tr className="bg-gray-50 text-gray-700 border-b">
                        <th
                          className="px-3 py-2 font-medium text-center sticky left-0 z-20 bg-white"
                          style={{ width: 40 }}
                        >
                          #
                        </th>
                        <th
                          className="px-3 py-2 font-medium sticky left-14 z-20 bg-white"
                          style={{ width: 90 }}
                        >
                          Image
                        </th>
                        <th
                          className="px-3 py-2 font-medium sticky left-40 z-20 bg-white"
                          style={{ minWidth: 220 }}
                        >
                          Identity
                        </th>
                        {subjectCols.map((col) => (
                          <th
                            key={col.subjectId}
                            className={`px-3 py-2 font-semibold text-center whitespace-nowrap align-top ${col.actualAssignmentSubmissionDate ? 'bg-emerald-50 text-emerald-800' : 'bg-gray-50 text-gray-700'}`}
                          >
                            <div className="flex flex-col items-center gap-1">
                              {col.lecturerImagePath && (
                                <img
                                  src={getImageURL(col.lecturerImagePath)}
                                  alt={col.lecturerName || 'Lecturer'}
                                  className="h-8 w-8 rounded-full object-cover border border-emerald-200"
                                />
                              )}
                              <div className="text-sm font-bold">{col.subjectCode}</div>
                              {col.lecturerName && (
                                <div className="text-[11px] text-emerald-800 truncate max-w-[120px]" title={col.lecturerName}>
                                  {col.lecturerName}
                                </div>
                              )}
                              {col.actualAssignmentSubmissionDate && (
                                <div className="text-[11px] text-emerald-700">{formatDate(col.actualAssignmentSubmissionDate)}</div>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {sortedStudents.map((s, idx) => {
                        const stats = computeStudentStats(s);
                        const attendancePct = getAttendancePercent(s);
                        const universityId = s.universityID || s.universityId || s.university_id || '';
                        const universityRegRaw = s.universityRegisterDate || s.university_register_date || '';
                        const universityInfo = getUniversityRegisterInfo(universityRegRaw);
                        return (
                          <tr key={`${s.studentId}-${idx}`} className="align-top">
                            <td className="px-3 py-3 text-center text-gray-800 sticky left-0 z-10 bg-white">{idx + 1}</td>
                            <td className="px-3 py-3 sticky left-14 z-10 bg-white">
                              <div className="flex flex-col items-center gap-2">
                                {s.imageUrl || s.imagePath ? (
                                  <img
                                    src={getImageURL(s.imagePath)}
                                    alt={s.fullName || 'student'}
                                    className="h-35 w-20 rounded-md object-cover border"
                                  />
                                ) : (
                                  <div className="h-16 w-16 rounded-md bg-gray-100 border flex items-center justify-center text-gray-500 text-xs">No photo</div>
                                )}
                                {isPlatformAdmin && (
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveStudent(s.studentId)}
                                    className="inline-flex items-center rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
                                  >
                                    Clear marks
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-3 text-gray-800 sticky left-40 z-10 bg-white">
                              <div className="font-semibold text-base">{s.fullName || s.name || 'Unknown'}</div>
                              <div className="text-xs text-gray-600">ID: {s.studentId || s.id || ''}</div>
                              <div className="text-xs text-gray-600">Tel: {s.telephone || s.phone || ''}</div>
                              <div className="text-xs text-gray-600">Reg #: {s.registrationNumber || s.student_registration_number || ''}</div>
                              {(universityId || universityInfo) && (
                                <div className="mt-1 flex flex-wrap gap-1 text-[11px] text-gray-600">
                                  {universityId && (
                                    <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-700 border border-slate-200">
                                      Uni ID: {universityId}
                                    </span>
                                  )}
                                  {universityInfo && (
                                    <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-700 border border-slate-200">
                                      Uni reg: {universityInfo.formattedDate}  b7 {universityInfo.monthsLabel}
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="mt-1 flex flex-wrap gap-1 items-center">
                                {universityInfo && (
                                  <span
                                    className={`inline-flex items-center rounded-md px-1.5 py-0.5 font-semibold border text-[10px] ${
                                      universityInfo.monthsCount >= 12
                                        ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                                        : 'bg-amber-50 text-amber-700 border-amber-200'
                                    }`}
                                  >
                                    {universityInfo.monthsCount >= 12 ? 'Eligible' : 'Not yet eligible'}
                                  </span>
                                )}
                                {s.registrationStatus && (
                                  <span className="inline-flex items-center rounded-md bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700 border border-green-200">
                                    {s.registrationStatus}
                                  </span>
                                )}
                              </div>
                              <div className="mt-2 flex flex-wrap gap-1">
                                {['D', 'M', 'P', 'R', 'SN', 'NS'].map((code) => (
                                  <span
                                    key={code}
                                    className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-semibold ${chipClass(code)}`}
                                  >
                                    {code}: {stats.counts[code] ?? 0}
                                  </span>
                                ))}
                              </div>
                              <div className="mt-3 space-y-1">
                                <div className="h-px bg-gray-200" />
                                <div className="flex items-center justify-between text-[11px] text-gray-600">
                                  <span>Total attendance</span>
                                  <span className="font-semibold text-gray-800">{attendancePct ?? 'N/A'}{attendancePct !== null ? '%' : ''}</span>
                                </div>
                                <div className="h-2 w-full rounded-full bg-gray-100 border border-gray-200 overflow-hidden">
                                  <div
                                    className="h-full transition-all"
                                    style={{
                                      width: `${attendancePct ?? 0}%`,
                                      opacity: attendancePct !== null ? 1 : 0.35,
                                      backgroundColor:
                                        attendancePct === null ? '#cbd5e1' :
                                        attendancePct >= 80 ? '#22c55e' :
                                        attendancePct >= 60 ? '#f59e0b' :
                                        '#ef4444',
                                    }}
                                  />
                                </div>
                              </div>
                            </td>
                            {subjectCols.map((col) => {
                              const code = displayForSubject(s, col);
                              return (
                                <td
                                  key={`${s.studentId}-${col.subjectId}`}
                                  className="px-2 py-3 text-center align-middle cursor-pointer hover:bg-indigo-50 transition-colors"
                                  title="Double-click to edit submission"
                                  onDoubleClick={() => openSubmissionModal(s, col)}
                                >
                                  <span className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${chipClass(code)}`}>
                                    {code}
                                  </span>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

      {editModal.open && (
        <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-2xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <div>
                <div className="text-lg font-semibold text-gray-900">Edit Assignment Submission</div>
                <div className="text-sm text-gray-600">{editModal.subject?.subjectCode || ''} • {editModal.subject?.subjectName || ''}</div>
              </div>
              <button
                type="button"
                className="p-2 text-gray-500 hover:text-gray-700"
                onClick={closeSubmissionModal}
              >
                ✕
              </button>
            </div>

            <div className="px-4 py-4 space-y-3 text-sm text-gray-800">
              <div className="rounded-md bg-gray-50 border border-gray-200 p-3">
                <div className="font-semibold text-gray-900">{editModal.student?.fullName || '—'}</div>
                <div className="text-xs text-gray-600">ID: {editModal.student?.studentId || '—'} · Reg #: {editModal.student?.registrationNumber || '—'}</div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-800">Submission Status</label>
                  <select
                    value={editModal.status}
                    onChange={(e) => setEditModal((prev) => ({ ...prev, status: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  >
                    {SUBMISSION_STATUS_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-800">Marks</label>
                  <select
                    value={editModal.marks ?? ''}
                    onChange={(e) => setEditModal((prev) => ({ ...prev, marks: e.target.value }))}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  >
                    {MARK_OPTIONS.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">Choose "Not Corrected Yet" to clear marks.</p>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
                onClick={closeSubmissionModal}
                disabled={savingSubmission}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60"
                onClick={handleSaveSubmission}
                disabled={savingSubmission}
              >
                {savingSubmission ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {uploadModal.open && (
        <div className="fixed inset-0 z-[1200] bg-white">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 bg-white">
              <div className="text-xl font-semibold text-gray-900">Upload ELMS Sheet</div>
              <button
                type="button"
                className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
                onClick={closeUploadModal}
                disabled={uploading}
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-4xl mx-auto space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select File
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    disabled={uploading}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer bg-gray-50 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50"
                  />
                </div>

                {uploadFile && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{uploadFile.name}</p>
                      <p className="text-xs text-gray-600">{(uploadFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                    {!uploading && (
                      <button
                        type="button"
                        onClick={() => {
                          setUploadFile(null);
                          setParsedStudents([]);
                          setParseError(null);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                )}

                {parseError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800">{parseError}</p>
                  </div>
                )}

                {parsingFile && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <div className="relative">
                      <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    </div>
                    <div className="text-center space-y-1">
                      <p className="text-base font-medium text-gray-900">Processing Excel file...</p>
                      <p className="text-sm text-gray-600">Matching students and extracting marks</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                      <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                      <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                    </div>
                  </div>
                )}

                {parsedStudents.length > 0 && (() => {
                  // Calculate statistics
                  let totalMatches = 0;
                  let totalMismatches = 0;
                  let totalNoExcelData = 0;
                  let studentsWithMismatches = 0;

                  parsedStudents.forEach(student => {
                    let studentHasMismatch = false;
                    subjectCols.forEach(col => {
                      const dbCode = displayForSubject(student, col);
                      const excelMark = excelMarks[student.studentId]?.[col.subjectCode];
                      const hasExcelMark = excelMark !== null && excelMark !== undefined && excelMark !== '';
                      
                      if (hasExcelMark) {
                        const marksMatch = normalizeMark(excelMark) === dbCode;
                        if (marksMatch) {
                          totalMatches++;
                        } else {
                          totalMismatches++;
                          studentHasMismatch = true;
                        }
                      } else {
                        totalNoExcelData++;
                      }
                    });
                    if (studentHasMismatch) studentsWithMismatches++;
                  });

                  return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">
                        Matched Students ({parsedStudents.length})
                      </h3>
                    </div>

                    {/* Statistics Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <div className="text-2xl font-bold text-blue-900">{parsedStudents.length}</div>
                        </div>
                        <div className="text-xs text-blue-700 mt-1">Students Found</div>
                      </div>

                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div className="text-2xl font-bold text-green-900">{totalMatches}</div>
                        </div>
                        <div className="text-xs text-green-700 mt-1">Matching Marks</div>
                      </div>

                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div className="text-2xl font-bold text-yellow-900">{totalMismatches}</div>
                        </div>
                        <div className="text-xs text-yellow-700 mt-1">Mismatches</div>
                      </div>

                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                          </svg>
                          <div className="text-2xl font-bold text-gray-900">{totalNoExcelData}</div>
                        </div>
                        <div className="text-xs text-gray-700 mt-1">No Excel Data</div>
                      </div>
                    </div>

                    {/* Mismatch Warning */}
                    {totalMismatches > 0 && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                        <div className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-yellow-800 mb-1">
                              ⚠️ Mark Mismatches Detected
                            </h4>
                            <p className="text-sm text-yellow-700 mb-2">
                              Found <strong>{totalMismatches}</strong> subject mark(s) where Excel data differs from database records across <strong>{studentsWithMismatches}</strong> student(s).
                            </p>
                            <p className="text-xs text-yellow-600">
                              Review the highlighted entries below (yellow background) and double-click Excel values to edit if needed before uploading.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Success Message */}
                    {totalMismatches === 0 && totalMatches > 0 && (
                      <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-md">
                        <div className="flex items-start gap-3">
                          <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <h4 className="text-sm font-semibold text-green-800 mb-1">
                              ✓ All Marks Match Successfully
                            </h4>
                            <p className="text-sm text-green-700">
                              All {totalMatches} marks in the Excel file match the existing database records. You can proceed with the upload.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {parsedStudents.map((student) => (
                        <div
                          key={student.studentId}
                          className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                        >
                          <div className="flex gap-4 p-4">
                            <div className="flex-shrink-0">
                              {student.imagePath ? (
                                <img
                                  src={getImageURL(student.imagePath)}
                                  alt={student.fullName || 'Student'}
                                  className="w-20 h-24 rounded-md object-cover border border-gray-200"
                                />
                              ) : (
                                <div className="w-20 h-24 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center">
                                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                  </svg>
                                </div>
                              )}
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <h4 className="text-base font-semibold text-gray-900 truncate mb-2">
                                {student.fullName || 'Unknown'}
                              </h4>
                              
                              <div className="space-y-1 text-xs text-gray-600">
                                <div className="flex items-start gap-2">
                                  <span className="font-medium min-w-[60px]">Reg No:</span>
                                  <span className="text-gray-900">{student.registrationNumber || '—'}</span>
                                </div>
                                <div className="flex items-start gap-2">
                                  <span className="font-medium min-w-[60px]">Student ID:</span>
                                  <span className="text-gray-900">{student.studentId || '—'}</span>
                                </div>
                                <div className="flex items-start gap-2">
                                  <span className="font-medium min-w-[60px]">Tel:</span>
                                  <span className="text-gray-900">{student.telephone || '—'}</span>
                                </div>
                                <div className="flex items-start gap-2">
                                  <span className="font-medium min-w-[60px]">Email:</span>
                                  <span className="text-gray-900 truncate">{student.email || '—'}</span>
                                </div>
                                <div className="flex items-start gap-2">
                                  <span className="font-medium min-w-[60px]">Course:</span>
                                  <span className="text-gray-900">{student.courseShortName || student.courseName || '—'}</span>
                                </div>
                                <div className="flex items-start gap-2">
                                  <span className="font-medium min-w-[60px]">Batch:</span>
                                  <span className="text-gray-900">{student.batchName || '—'}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Student Marks Section */}
                          {subjectCols.length > 0 && (
                            <div className="border-t border-gray-200 px-4 py-3 bg-gray-50">
                              <div className="text-xs font-semibold text-gray-700 mb-2">Assignment Marks Comparison:</div>
                              <div className="space-y-2">
                                {subjectCols.map((col) => {
                                  const dbCode = displayForSubject(student, col);
                                  const excelMark = excelMarks[student.studentId]?.[col.subjectCode];
                                  const hasExcelMark = excelMark !== null && excelMark !== undefined && excelMark !== '';
                                  const normalizedExcel = hasExcelMark ? normalizeMark(excelMark) : '';
                                  const marksMatch = hasExcelMark && normalizedExcel === dbCode;

                                  // Detect special composite Excel patterns that we normalize
                                  let compositeRule = null;
                                  if (hasExcelMark) {
                                    const rawUpper = String(excelMark).trim().toUpperCase().replace(/\s+/g, '');
                                    if (rawUpper === 'R,RP') compositeRule = 'R,RP → P';
                                    else if (rawUpper === 'R,R,RN') compositeRule = 'R,R,RN → R';
                                    else if (rawUpper === 'R,RF') compositeRule = 'R,RF → R';
                                  }
                                  
                                  // Get feedback/notes for this subject (if available in student data)
                                  const feedback = student.feedbackBySubject?.[col.subjectCode];
                                  
                                  return (
                                    <div
                                      key={col.subjectId}
                                      className={`flex flex-col gap-2 p-2 rounded-md ${
                                        hasExcelMark
                                          ? compositeRule
                                            ? 'bg-sky-50 border border-sky-300'
                                            : marksMatch
                                              ? 'bg-green-50 border border-green-200'
                                              : 'bg-yellow-50 border border-yellow-200'
                                          : 'bg-white border border-gray-200'
                                      }`}
                                      title={col.subjectName}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs font-medium text-gray-700 min-w-[40px]">{col.subjectCode}</span>
                                          {hasExcelMark && !marksMatch && (
                                            <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                          )}
                                          {hasExcelMark && marksMatch && (
                                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                          )}
                                        </div>
                                        
                                        <div className="flex items-center gap-3">
                                          <div className="text-right">
                                            <div className="text-[10px] text-gray-500 uppercase">DB</div>
                                            <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold ${chipClass(dbCode)}`}>
                                              {dbCode}
                                            </span>
                                          </div>
                                          
                                          {hasExcelMark && (
                                            <>
                                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                              </svg>
                                              <div className="text-right">
                                                <div className="text-[10px] text-blue-600 uppercase font-medium">Excel</div>
                                                {editingExcelMark?.studentId === student.studentId && editingExcelMark?.subjectCode === col.subjectCode ? (
                                                  <div className="flex items-center gap-1">
                                                    <input
                                                      type="text"
                                                      value={editingExcelValue}
                                                      onChange={(e) => setEditingExcelValue(e.target.value)}
                                                      onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                          handleSaveExcelMark(student.studentId, col.subjectCode);
                                                        } else if (e.key === 'Escape') {
                                                          handleCancelExcelEdit();
                                                        }
                                                      }}
                                                      onBlur={() => handleSaveExcelMark(student.studentId, col.subjectCode)}
                                                      autoFocus
                                                      className="w-12 px-1 py-0.5 text-xs text-center border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    />
                                                    <button
                                                      onClick={() => handleSaveExcelMark(student.studentId, col.subjectCode)}
                                                      className="text-green-600 hover:text-green-800"
                                                    >
                                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                      </svg>
                                                    </button>
                                                    <button
                                                      onClick={handleCancelExcelEdit}
                                                      className="text-red-600 hover:text-red-800"
                                                    >
                                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                      </svg>
                                                    </button>
                                                  </div>
                                                ) : (
                                                  <span 
                                                    className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-semibold cursor-pointer hover:ring-2 hover:ring-blue-400 ${chipClass(normalizedExcel || normalizeMark(excelMark))}`}
                                                    onDoubleClick={() => handleEditExcelMark(student.studentId, col.subjectCode, excelMark)}
                                                    title="Double-click to edit"
                                                  >
                                                    {normalizedExcel || excelMark}
                                                  </span>
                                                )}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                      {/* Excel normalization note (for composite patterns) */}
                                      {compositeRule && (
                                        <div className="mt-2 pt-2 border-t border-sky-200">
                                          <div className="flex items-center gap-1 mb-1">
                                            <svg className="w-3 h-3 text-sky-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m2-4h.01M12 4a8 8 0 100 16 8 8 0 000-16z" />
                                            </svg>
                                            <span className="text-[10px] text-sky-700 uppercase font-medium">Excel rule applied</span>
                                          </div>
                                          <div className="text-[11px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-2 py-1.5">
                                            Excel value <span className="font-semibold">"{String(excelMark).trim()}"</span> interpreted as <span className="font-semibold">{normalizedExcel}</span> ({compositeRule}).
                                          </div>
                                        </div>
                                      )}

                                      {/* Lecturer Feedback/Notes - Only show if not matched */}
                                      {(!hasExcelMark || !marksMatch) && (
                                        <div className="mt-2 pt-2 border-t border-gray-200">
                                          <div className="flex items-center justify-between mb-1">
                                            <div className="flex items-center gap-1">
                                              <svg className="w-3 h-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                              </svg>
                                              <span className="text-[10px] text-gray-500 uppercase font-medium">Lecturer Note</span>
                                            </div>
                                            {hasExcelMark && !marksMatch && (
                                              <div className="flex items-center gap-1">
                                                <svg className="w-3 h-3 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                </svg>
                                                <span className="text-[9px] text-orange-700 font-semibold uppercase">Mismatch</span>
                                              </div>
                                            )}
                                          </div>
                                          <div className={`text-xs rounded px-2 py-1.5 ${
                                            hasExcelMark && !marksMatch
                                              ? 'bg-orange-50 border border-orange-300 text-gray-800'
                                              : feedback && feedback.trim() !== '' 
                                              ? 'text-gray-800 bg-blue-50 border border-blue-200' 
                                              : 'text-gray-400 italic bg-gray-50 border border-gray-100'
                                          }`}>
                                            {hasExcelMark && !marksMatch && (
                                              <div className="text-[10px] text-orange-700 font-medium mb-1">
                                                ⚠️ Mark mismatch detected - Review recommended
                                              </div>
                                            )}
                                            {feedback && feedback.trim() !== '' ? feedback : 'No lecturer note given'}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })()}

                {uploading && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-700">Uploading...</span>
                      <span className="text-indigo-600">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-indigo-600 h-2 rounded-full transition-all"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
              <button
                type="button"
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                onClick={closeUploadModal}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
              >
                {uploading ? 'Updating...' : 'Update Mismatched Marks'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
}
